import type { FC } from 'react';
import { memo, useRef } from 'react';
import { Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import type { KanbanWorkOrderView } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/types';
import type { KanbanColumnStatus } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/utils';
import { WorkOrderKanbanCard } from '@evimo/components/maintenance/work-orders/WorkOrderKanbanPageContainer/components/WorkOrderKanbanCard/WorkOrderKanbanCard';
import {
  KANBAN_COLUMN_MIN_WIDTH,
  WORK_ORDER_UI,
  columnHeaderTone,
} from '~/app/components/maintenance/work-orders/work-order-ui-colors';
import { useKanbanColumnVirtualization } from './hooks/useKanbanColumnVirtualization';
import { KanbanColumnLane } from './KanbanColumnLane';

/** Stable for the whole drag session — set only on drag start/end, never on move. */
export type KanbanActiveDragKind = 'workOrder' | 'ticket' | null;

export interface WorkOrderKanbanColumnProps {
  status: KanbanColumnStatus;
  title: string;
  description: string;
  workOrders: KanbanWorkOrderView[];
  canManage: boolean;
  organizationId: string;
  selectedWorkOrderId: string | null;
  /** From board `activeDrag` — must NOT come from useDndContext (re-renders every move). */
  activeDragKind: KanbanActiveDragKind;
  /** Entity id of the active drag source — used to keep only that item subscribed. */
  activeDragId: string | null;
  onSelectWorkOrder: (workOrderId: string) => void;
  onOpenMoveTicket: (workOrderId: string, ticketId: string) => void;
  /**
   * Future cursor pagination — call when the user nears the end of the lane.
   * No-op in V1 (full board fetch).
   */
  onReachEnd?: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
}

export const WorkOrderKanbanColumn: FC<WorkOrderKanbanColumnProps> = memo(
  function WorkOrderKanbanColumn({
    status,
    title,
    description,
    workOrders,
    canManage,
    organizationId,
    selectedWorkOrderId,
    activeDragKind,
    activeDragId,
    onSelectWorkOrder,
    onOpenMoveTicket,
    onReachEnd,
    hasNextPage = false,
    isFetchingNextPage = false,
  }) {
    const { t } = useTranslation('maintenance');
    const scrollParentRef = useRef<HTMLDivElement | null>(null);
    const isWorkOrderDrag = activeDragKind === 'workOrder';
    const isTicketDrag = activeDragKind === 'ticket';
    const columnAcceptsTicketClose =
      isTicketDrag && status === 'CLOSED' && canManage;

    const tone = columnHeaderTone(status);

    const { virtualizer, virtualItems, totalSize } =
      useKanbanColumnVirtualization({
        workOrders,
        scrollParentRef,
        onReachEnd,
        hasNextPage,
        isFetchingNextPage,
      });

    return (
      <div
        style={{
          minWidth: KANBAN_COLUMN_MIN_WIDTH,
          width: KANBAN_COLUMN_MIN_WIDTH,
          flex: `0 0 ${KANBAN_COLUMN_MIN_WIDTH}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          minHeight: 0,
          height: '100%',
        }}
      >
        <div
          style={{
            flexShrink: 0,
            background: tone.bg,
            color: tone.fg,
            borderRadius: 12,
            padding: '12px 14px',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 10,
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <Typography.Text
              strong
              style={{
                display: 'block',
                color: tone.fg,
                fontSize: 14,
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography.Text>
            <Typography.Text
              style={{
                display: 'block',
                marginTop: 2,
                color: tone.fg,
                opacity: 0.72,
                fontSize: 11,
                lineHeight: 1.35,
              }}
            >
              {description}
            </Typography.Text>
          </div>
          <span
            aria-label={String(workOrders.length)}
            style={{
              flexShrink: 0,
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: '#fff',
              color: tone.badge,
              fontWeight: 700,
              fontSize: 12,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 0 0 2px rgba(255,255,255,0.55)',
            }}
          >
            {workOrders.length}
          </span>
        </div>

        <KanbanColumnLane
          status={status}
          droppable={isWorkOrderDrag || isTicketDrag}
          scrollParentRef={scrollParentRef}
        >
          {({ isOver }) => {
            const columnAcceptsDrop =
              isOver && (isWorkOrderDrag || columnAcceptsTicketClose);
            return (
              <div
                style={{
                  height: totalSize + (isWorkOrderDrag ? 80 : 56),
                  width: '100%',
                  position: 'relative',
                }}
              >
                {virtualItems.map((virtualRow) => {
                  const workOrder = workOrders[virtualRow.index];
                  if (!workOrder) return null;
                  return (
                    <div
                      key={workOrder.id}
                      data-index={virtualRow.index}
                      ref={virtualizer.measureElement}
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        transform: `translateY(${virtualRow.start}px)`,
                        paddingBottom: 10,
                      }}
                    >
                      <WorkOrderKanbanCard
                        workOrder={workOrder}
                        canManage={canManage}
                        organizationId={organizationId}
                        selected={selectedWorkOrderId === workOrder.id}
                        activeDragKind={activeDragKind}
                        activeDragId={activeDragId}
                        onSelectWorkOrder={onSelectWorkOrder}
                        onOpenMoveTicket={onOpenMoveTicket}
                      />
                    </div>
                  );
                })}

                <div
                  style={{
                    position: 'absolute',
                    top: totalSize,
                    left: 0,
                    right: 0,
                    minHeight: isWorkOrderDrag ? 72 : 48,
                    borderRadius: 10,
                    border: isWorkOrderDrag
                      ? `1px dashed ${
                          columnAcceptsDrop ? WORK_ORDER_UI.primary : '#cbd5e1'
                        }`
                      : '1px dashed transparent',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: columnAcceptsDrop
                      ? WORK_ORDER_UI.primary
                      : '#94a3b8',
                    fontSize: 12,
                    fontWeight: 500,
                    pointerEvents: 'none',
                  }}
                >
                  {isWorkOrderDrag
                    ? t('workOrders.kanban.drag.dropWorkOrderHere')
                    : columnAcceptsTicketClose
                      ? t('workOrders.kanban.drag.dropTicketToClose')
                      : null}
                </div>
              </div>
            );
          }}
        </KanbanColumnLane>
      </div>
    );
  },
);
